/**
 * Runtime config. Override at build time via env vars:
 *   VITE_API_BASE_URL=https://api.wattr.ai
 *   VITE_DEMO_MODE=false
 *
 * Requires a rebuild + `cap sync` for native shells to pick up new values.
 */

// In dev, default to '' so requests go through the Vite proxy (vite.config.ts)
// to avoid CORS. In prod builds, default to the staging API until promoted.
const fromEnv = (import.meta.env.VITE_API_BASE_URL as string | undefined)?.replace(/\/$/, '');
const fallback = import.meta.env.DEV ? '' : 'https://staging-api.wattr.ai';
export const API_BASE_URL = fromEnv ?? fallback;

import { Capacitor } from '@capacitor/core';

/**
 * Web-preview mode — true whenever the app is running in a browser
 * (vite dev server, deployed web preview) and NEVER true on a native
 * APK install. Used so the team can click through the survey to inspect
 * every step + feature without filling required fields. Native APK is
 * the only surface real submissions can leave the device from, and
 * Capacitor.isNativePlatform() is the load-bearing guard that keeps
 * this from ever turning on by accident in production.
 */
export const WEB_PREVIEW = !Capacitor.isNativePlatform();

/**
 * Demo mode — skips all `required` checks so a rep can step through every
 * section without filling in fields. Opt-in via VITE_DEMO_MODE=true, OR
 * automatic whenever WEB_PREVIEW is on (May 30 product call: web previews
 * are dev-only walkthroughs and shouldn't gate progression).
 *
 * The anti-blank gate in validateForSubmit() additionally honours
 * WEB_PREVIEW directly so a browser preview can submit a fully-empty
 * form for inspection purposes. Native APK builds remain locked down —
 * see the May 2026 field report ("submission got approved without any
 * entry") for the incident that prompted the original guard.
 */
export const DEMO_MODE =
  (import.meta.env.VITE_DEMO_MODE as string | undefined) === 'true'
  || WEB_PREVIEW;

/**
 * Installer feature flag — gates the installer/field-test login surface
 * (splash button + `/installer/*` routes). OFF by default; the code is
 * preserved and reactivated later by setting VITE_INSTALLER_ENABLED=true
 * and rebuilding (+ `cap sync` for native shells). Kept as its own flag
 * rather than deleting the merged installer flow so re-enabling is a
 * one-line env change with zero code churn.
 */
export const INSTALLER_ENABLED =
  (import.meta.env.VITE_INSTALLER_ENABLED as string | undefined) === 'true';
